/**
 * Englische Texte.
 *
 * Der Typ ist an die Schlüssel von `de` gebunden: fehlt hier eine Zeile, meldet es
 * der Compiler.
 * Das ist der Grund, warum die Übersetzungen als TypeScript und nicht als JSON
 * vorliegen — eine fehlende Zeile fällt beim Bauen auf und nicht im Betrieb.
 */
import type { TranslationKey } from "./de.js";

export const en: Readonly<Record<TranslationKey, string>> = {
  "quantity.consumption": "Consumption",
  "quantity.grid_import": "Grid import",
  "quantity.grid_export": "Grid export",
  "quantity.generation": "Generation",
  "quantity.self_consumption": "Self-consumption",
  "quantity.autarky_ratio": "Self-sufficiency",
  "quantity.self_consumption_ratio": "Self-consumption rate",
  "quantity.common_area": "Common area",
  "quantity.unit_consumption": "Unit consumption",
  "quantity.unit_pv_share": "Unit PV share",
  "quantity.unit_grid_share": "Unit grid share",
  "quantity.load_consumption": "Load consumption",
  "quantity.load_pv_share": "Load PV share",
  "quantity.residual_consumption": "Remaining consumption",
  "quantity.storage_charge": "Storage charge",
  "quantity.storage_discharge": "Storage discharge",
  "generationAsset.kind.pv": "PV system",
  "generationAsset.kind.battery": "Battery storage",
  "consumer.kind.common_area": "Common area",
  "consumer.kind.heat_pump": "Heat pump",
  "consumer.kind.wallbox": "Wallbox",
  "consumer.kind.other": "Other load",
  "consumer.position.before_own_unit": "before the owner-occupied unit",
  "consumer.position.behind_own_unit": "behind the owner-occupied unit",
  "distribution.dynamic": "Dynamic allocation",
  "distribution.dynamic.description":
    "Every quarter hour, each unit receives the share of PV power that matches its share of simultaneous consumption. Tracks simultaneity exactly and leaves nothing unused — but a large load crowds out small ones in the billing, too.",
  "distribution.equal_fill": "Equal split with redistribution of the remainder",
  "distribution.equal_fill.description":
    "Every quarter hour, PV power is first split equally; whoever consumes less than their share receives only their consumption, and the remainder goes to those with demand still open. Everyone gets a floor, the export is the same. No provider has a name for it — the method must be spelled out in the contract.",
  "distribution.annual_sum": "Sum-meter model (VBEW)",
  "distribution.annual_sum.description":
    "Simplified balancing: only how much PV power stayed in the building is known, not where. That quantity is allocated by share of consumption over the billing period — no quarter-hour values, blind to timing. Not permitted for shared building supply under § 42b EnWG.",

  "role.energy_import_total": "Total import",
  "role.energy_import_t1": "Import tariff 1",
  "role.energy_import_t2": "Import tariff 2",
  "role.energy_export_total": "Total export",
  "role.power_total": "Instantaneous power",
  "role.power_l1": "Power L1",
  "role.power_l2": "Power L2",
  "role.power_l3": "Power L3",
  "role.voltage_l1": "Voltage L1",
  "role.voltage_l2": "Voltage L2",
  "role.voltage_l3": "Voltage L3",
  "role.current_l1": "Current L1",
  "role.current_l2": "Current L2",
  "role.current_l3": "Current L3",
  "role.heat_energy": "Heat energy",
  "role.heat_power": "Heat power",
  "role.volume": "Volume",
  "role.volume_flow": "Flow rate",
  "role.temperature_flow": "Flow temperature",
  "role.temperature_return": "Return temperature",
  "role.temperature_difference": "Temperature difference",

  "meter.status.draft": "Incomplete",
  "meter.status.active": "Active",
  "meter.status.replaced": "Replaced",
  "meter.status.archived": "Archived",
  "meter.number.missing": "Meter number missing",
  "meter.number.hint":
    "The meter is already collecting data. Reports and billing still need the meter number from its label.",

  "meter_change.pending.title": "Meter change detected",
  "meter_change.pending.body":
    "A different meter appears to be connected here. Please confirm and enter the new meter number.",
  "meter_change.confirm": "Confirm change",
  "meter_change.reject": "Not a change",

  "integration.cts_cloud.title": "CTS devices via the HausMeter cloud",
  "integration.cts_cloud.description":
    "The device sends its readings to HausMeter. Years of history, analysis across several devices.",
  "integration.cts_local.title": "CTS device on your own network",
  "integration.cts_local.description":
    "The app talks to the device directly. Nothing leaves your home — but it only keeps a few days of history.",
  "integration.manual_input.title": "Manual readings",
  "integration.manual_input.description":
    "No device needed: enter readings yourself. Also a fallback when a device fails.",

  "setup.claim.title": "Link device",
  "setup.confirm.title": "Confirm",
  "setup.discover.title": "Find device",
  "setup.ble.title": "Set up via Bluetooth",
  "setup.softap.title": "Set up via the device network",
  "setup.form.title": "Details",

  "mode.cloud": "Cloud",
  "mode.local": "Local only",
  "mode.hybrid": "Local and cloud",
  "mode.local.hint":
    "Live readings come straight from the device. History beyond a few days and measurement concepts need the cloud.",

  "series.gap": "No data",
  "series.quality.interpolated": "Estimated value",
  "series.quality.suspect": "Implausible value",

  "error.auth.required": "Please sign in.",
  "error.auth.forbidden": "You do not have permission for this.",
  "error.resource.not_found": "Not found.",
  "error.resource.conflict": "This already exists.",
  "error.feature.not_implemented": "This feature is still being built.",
  "error.internal.error": "Something went wrong. Please try again later.",
  "error.network": "No connection.",
};
